// 战绩 and 排行榜 (office/records.ts, src/records.ts): one panel for every game's records, opened from each game's own
// panel (recordsButton(game)) or openRecords(game). Three views: 排行榜 (this week or all time, by wins, win rate,
// points or best streak), 我的战绩 (my totals and my last rounds), 最近对局 (the last rounds at this server). The
// office answers {t:"rec_get"} with {t:"rec"}; names only, my own lines marked `me`. app.js hands it the socket
// (initRecords) and the "rec" messages (recordsMsg). Text: catalog keys rec.* (public/locales).
import { t, onLang, fmtNumber, fmtRelative } from '/i18n.js';
import { icon, label } from '/icons.js';

const CSS_URL = new URL('./records.css', import.meta.url).pathname; // its URL: Vite names the built file by its content (vite.config.mjs)
if (typeof document !== 'undefined' && !document.querySelector(`link[href="${CSS_URL}"]`)) {
  const l = document.createElement('link'); l.rel = 'stylesheet'; l.href = CSS_URL; document.head.appendChild(l);
}
const el = (tag, cls, text) => { const e = document.createElement(tag); if (cls) e.className = cls; if (text != null) e.textContent = text; return e; };

/** Every game with records, in the picker's order: its icon, and what its points are (none: no 积分 column). */
export const REC_GAMES = {
  gomoku: { icon: 'stones' }, othello: { icon: 'othello' }, go: { icon: 'go' }, xiangqi: { icon: 'xiangqi' }, chess: { icon: 'chess_n' },
  holdem: { icon: 'cards', points: 'rec.points.chips' }, doudizhu: { icon: 'cards', points: 'rec.points.score' }, mahjong: { icon: 'cards', points: 'rec.points.score' },
  pictionary: { icon: 'palette', points: 'rec.points.score' }, spy: { icon: 'spy', points: 'rec.points.score' }, werewolf: { icon: 'wolf' },
  otterbonk: { icon: 'sword', points: 'rec.points.kills' }, mind: { icon: 'heart', points: 'rec.points.score' },
};
/** The views, the periods and the sorts, as the panel's buttons show them. */
const VIEWS = { top: 'rec.view.top', mine: 'rec.view.mine', recent: 'rec.view.recent' };
const PERIODS = { week: 'rec.period.week', all: 'rec.period.all' };
const SORTS = { wins: 'rec.sort.wins', rate: 'rec.sort.rate', points: 'rec.sort.points', streak: 'rec.sort.streak' };
const RESULT = { win: 'rec.result.win', loss: 'rec.result.loss', draw: 'rec.result.draw' };

let send = null, panel = null, n = 0;
const st = { game: 'gomoku', view: 'top', period: 'week', sort: 'wins', data: null, error: null, loading: false };

/** app.js: the socket's send. */
export function initRecords(o) {
  send = o.send;
  // guests and visitors from another server don't see this server's records (the office refuses rec_get)
  if (o.off) document.body.classList.add('rec-off');
}
/** A small 战绩 button for a game's panel (`game`: an id, or a function giving the one on now). */
export function recordsButton(game, cls = 'small') {
  const b = el('button', 'recbtn ' + cls);
  const draw = () => { label(b, 'trophy', t('rec.button')); b.title = t('rec.buttonTitle'); };
  draw(); onLang(draw);
  b.onclick = (e) => { e.stopPropagation(); openRecords(typeof game === 'function' ? game() : game); };
  return b;
}
/** Open the panel on `game`'s 排行榜 (or the last view I had). */
export function openRecords(game) {
  if (game && REC_GAMES[game]) { if (st.game !== game) st.data = null; st.game = game; if (st.sort === 'points' && !REC_GAMES[game].points) st.sort = 'wins'; }
  build(); panel.style.display = ''; ask();
}
export function closeRecords() { if (panel) panel.style.display = 'none'; }
/** The office's answer. */
export function recordsMsg(m) {
  if (m.n !== n) return; // an older question's
  st.loading = false;
  if (m.error) { st.error = m.error; } else { st.error = null; st.data = m; }
  render();
}
function ask() {
  if (!send) return;
  st.loading = true; st.error = null; n++;
  const mine = n;
  clearTimeout(ask.t); ask.t = setTimeout(() => { if (n === mine && st.loading) recordsMsg({ n: mine, error: 'E_UNAVAILABLE' }); }, 15000); // no answer (the socket dropped, an older office)
  send({ t: 'rec_get', n, view: st.view, game: st.game, period: st.period, sort: st.sort });
  render();
}

// ---- the panel
let ui = null;
function build() {
  if (panel) return;
  panel = el('div', 'recpanel'); panel.style.display = 'none';
  const head = el('div', 'rechead'), title = el('b', 'rectitle'), close = el('button', 'recclose');
  head.append(title, close);
  const games = el('div', 'recgames'), views = el('div', 'rectabs'), opts = el('div', 'recopts'), body = el('div', 'recbody');
  panel.append(head, games, views, opts, body);
  document.body.appendChild(panel);
  close.onclick = closeRecords;
  panel.addEventListener('keydown', (e) => { if (e.key === 'Escape') { closeRecords(); e.stopPropagation(); } });
  ui = { title, close, games, views, opts, body };
  onLang(render);
}
const pick = (box, map, cur, on, extra = {}) => {
  box.replaceChildren(...Object.entries(map).map(([id, key]) => {
    const b = el('button', id === cur ? 'on' : '');
    if (extra[id]) label(b, extra[id], t(key)); else b.textContent = t(key);
    b.onclick = () => on(id);
    return b;
  }));
};
const gameName = (g) => t(`rec.game.${g}`);
const pct = (r) => fmtNumber(r, { style: 'percent', maximumFractionDigits: 0 });
const pts = (v) => (v > 0 ? '+' : '') + fmtNumber(v);
function render() {
  if (!panel) return;
  label(ui.title, 'trophy', t('rec.title'));
  ui.close.textContent = t('rec.close');
  const g = REC_GAMES[st.game];
  pick(ui.games, Object.fromEntries(Object.keys(REC_GAMES).map((id) => [id, `rec.game.${id}`])), st.game,
    (id) => { st.game = id; if (st.sort === 'points' && !REC_GAMES[id].points) st.sort = 'wins'; ask(); }, Object.fromEntries(Object.entries(REC_GAMES).map(([id, x]) => [id, x.icon])));
  pick(ui.views, VIEWS, st.view, (v) => { st.view = v; ask(); });
  ui.opts.replaceChildren();
  if (st.view === 'top') {
    const per = el('div', 'recpick'), sort = el('div', 'recpick');
    pick(per, PERIODS, st.period, (p) => { st.period = p; ask(); });
    const sorts = g.points ? SORTS : Object.fromEntries(Object.entries(SORTS).filter(([k]) => k !== 'points'));
    pick(sort, sorts, st.sort, (s) => { st.sort = s; ask(); });
    ui.opts.append(per, sort);
  }
  const d = st.data;
  ui.body.replaceChildren();
  if (st.error) { ui.body.append(el('div', 'recempty', t(st.error === 'E_RATE' ? 'rec.err.rate' : 'rec.err.unavailable'))); return; }
  if (!d || d.view !== st.view || d.game !== st.game) { ui.body.append(el('div', 'recempty', t('rec.loading'))); return; }
  if (st.view === 'top') board(d.rows ?? [], g);
  else if (st.view === 'mine') mine(d.totals ?? [], d.rounds ?? []);
  else rounds(ui.body, d.rounds ?? []);
}
function board(rows, g) {
  if (!rows.length) { ui.body.append(el('div', 'recempty', t(st.period === 'week' ? 'rec.empty.week' : 'rec.empty.all'))); return; }
  const tb = el('table', 'rectable'), hr = el('tr');
  const cols = ['rec.col.rank', 'rec.col.name', 'rec.col.played', 'rec.col.wins', 'rec.col.rate', ...(g.points ? [g.points] : []), 'rec.col.best'];
  for (const c of cols) hr.append(el('th', '', t(c)));
  tb.append(hr);
  for (const r of rows) {
    const tr = el('tr', r.me ? 'me' : '');
    const rank = el('td', 'recrank');
    if (r.rank >= 1 && r.rank <= 3) rank.append(icon('medal_' + r.rank)); else rank.textContent = r.rank ? String(r.rank) : '-';
    const name = el('td', 'recname');
    if (r.kind === 'agent') name.append(icon('robot'), ' ');
    name.append(r.me ? t('rec.you', { name: r.name }) : r.name);
    tr.append(rank, name, el('td', '', fmtNumber(r.played)), el('td', '', fmtNumber(r.wins)), el('td', '', pct(r.rate)),
      ...(g.points ? [el('td', '', pts(r.points))] : []), el('td', '', fmtNumber(r.best)));
    tb.append(tr);
  }
  ui.body.append(tb);
  if (st.sort === 'rate') ui.body.append(el('div', 'recnote', t('rec.rateMin', { n: 5 })));
}
function mine(totals, list) {
  if (!totals.length && !list.length) { ui.body.append(el('div', 'recempty', t('rec.empty.mine'))); return; }
  for (const x of totals) {
    const card = el('div', 'recme'), xg = REC_GAMES[x.game] ?? {};
    const h = el('div', 'recmehead'); label(h, xg.icon ?? 'trophy', gameName(x.game));
    const stats = el('div', 'recstats');
    const stat = (k, v) => { const s = el('div', 'recstat'); s.append(el('b', '', v), el('span', '', t(k))); return s; };
    stats.append(stat('rec.col.played', fmtNumber(x.played)), stat('rec.stat.wins', fmtNumber(x.wins)), stat('rec.stat.losses', fmtNumber(x.losses)),
      ...(x.draws ? [stat('rec.stat.draws', fmtNumber(x.draws))] : []), stat('rec.col.rate', pct(x.rate)),
      ...(xg.points ? [stat(xg.points, pts(x.points))] : []), stat('rec.col.best', fmtNumber(x.best)));
    card.append(h, stats, el('div', 'recweek', t('rec.thisWeek', { played: x.week?.played ?? 0, wins: x.week?.wins ?? 0 })));
    ui.body.append(card);
  }
  if (list.length) { ui.body.append(el('div', 'recsub', t('rec.myRounds'))); rounds(ui.body, list); }
}
/** One line of facts about a round, from its summary. */
function facts(r) {
  const s = r.summary ?? {}, out = [];
  if (s.reason && r.game !== 'otterbonk') {
    const key = s.reason === 'stalemate' ? (r.game === 'chess' ? 'rec.reason.stalemateChess' : 'rec.reason.stalemate') : `rec.reason.${s.reason}`;
    out.push(t(key));
  }
  if (s.opts) out.push(String(s.opts));
  if (typeof s.moves === 'number') out.push(t('rec.moves', { n: s.moves }));
  if (s.score) out.push(String(s.score));
  if (r.game === 'spy' && s.winner) out.push(t(s.winner === 'spies' ? 'rec.spy.spiesWon' : 'rec.spy.civiliansWon'));
  if (r.game === 'spy' && s.civilian) out.push(t('rec.spy.words', { civilian: s.civilian, spy: s.spy ?? '' }));
  if (r.game === 'werewolf' && s.winner) out.push(t(`rec.ww.${s.winner === 'wolves' ? 'wolves' : s.winner === 'good' ? 'good' : 'draw'}`), t('rec.ww.days', { n: s.days ?? 0 }));
  if (r.game === 'holdem' && typeof s.pot === 'number') out.push(t('rec.holdem.pot', { n: s.pot }), ...(s.showdown ? [t('rec.holdem.showdown')] : []));
  if (r.game === 'doudizhu' && s.winner) out.push(t(s.winner === 'landlord' ? 'rec.ddz.landlord' : 'rec.ddz.farmers'), ...(s.mult ? [t('rec.ddz.mult', { n: s.mult })] : []), ...(s.spring ? [t(s.spring === 'anti' ? 'rec.ddz.anti' : 'rec.ddz.spring')] : []));
  if (r.game === 'mahjong' && s.end) out.push(s.end === 'hu' ? t(s.self ? 'rec.mj.self' : 'rec.mj.hu', { n: s.fan ?? 0 }) : s.end === 'xz' && s.wins ? t('rec.mj.xz', { n: s.wins }) : t(s.end === 'left' ? 'rec.mj.left' : 'rec.mj.draw'));
  if (r.game === 'pictionary' && s.rounds) out.push(t('rec.pict.rounds', { n: s.rounds }));
  if (r.game === 'mind' && s.rounds) out.push(t('rec.mind.rounds', { n: s.rounds }));
  if (r.game === 'otterbonk' && s.reason) out.push(t(`rec.ob.${['boss', 'down', 'timeout', 'home'].includes(s.reason) ? s.reason : 'down'}`), t('rec.ob.level', { n: s.level ?? 0 }));
  if (r.ms) out.push(t('rec.minutes', { n: Math.max(1, Math.round(r.ms / 60000)) }));
  return out.join(' · ');
}
const SIDE = { spy: 'rec.side.spy', civ: 'rec.side.civ', wolf: 'rec.side.wolf', seer: 'rec.side.seer', witch: 'rec.side.witch', villager: 'rec.side.villager',
  landlord: 'rec.side.landlord', farmer: 'rec.side.farmer' };
function rounds(box, list) {
  if (!list.length) { box.append(el('div', 'recempty', t('rec.empty.recent'))); return; }
  const ul = el('div', 'reclist');
  for (const r of list) {
    const row = el('div', 'recround'), top = el('div', 'recrtop');
    top.append(el('span', 'recwhen', fmtRelative(r.at)));
    if (r.game !== st.game) { const gn = el('span', 'recgame'); label(gn, REC_GAMES[r.game]?.icon ?? 'trophy', gameName(r.game)); top.append(gn); }
    if (r.place) top.append(el('span', 'recplace', r.place));
    const who = el('div', 'recwho');
    for (const p of r.players) {
      const c = el('span', 'recp ' + (p.result ?? '') + (p.me ? ' me' : ''));
      if (p.kind === 'agent') c.append(icon('robot'));
      c.append(p.name);
      if (p.side && SIDE[p.side]) c.append(el('i', '', t(SIDE[p.side])));
      if (p.result) c.append(el('b', '', t(RESULT[p.result])));
      if (p.points) c.append(el('em', '', pts(p.points)));
      who.append(c);
    }
    row.append(top, who);
    const f = facts(r); if (f) row.append(el('div', 'recfacts', f));
    ul.append(row);
  }
  box.append(ul);
}
